import { DELETED_ITEM_TYPES_FOR_STORE } from "./slice";

const CANONICAL_SET = new Set(DELETED_ITEM_TYPES_FOR_STORE);

/** Backend / API keys that may not match UI labels exactly */
const ALIAS_TO_CANONICAL = {
  company: "Company",
  contact: "Contact",
  opportunity: "Opportunity",
  document: "Document",
  product_service: "Product/Service",
  productservice: "Product/Service",
  products_services: "Product/Service",
  dashboard: "Dashboard",
  task: "Task",
  template: "Template",
  workflow: "Workflow",
  sequence: "Sequence",
  document_wizard_definition: "Document Wizard Definition",
  documentwizarddefinition: "Document Wizard Definition"
};

function resolveCanonicalModuleKey(rawKey) {
  if (rawKey === null || rawKey === undefined) {
    return null;
  }
  const key = String(rawKey).trim();
  if (CANONICAL_SET.has(key)) {
    return key;
  }
  const lower = key.toLowerCase();
  if (ALIAS_TO_CANONICAL[lower]) {
    return ALIAS_TO_CANONICAL[lower];
  }
  const compact = key.replace(/[\s/_-]+/g, "").toLowerCase();
  if (ALIAS_TO_CANONICAL[compact]) {
    return ALIAS_TO_CANONICAL[compact];
  }
  const ci = DELETED_ITEM_TYPES_FOR_STORE.find((t) => t.toLowerCase() === lower);
  return ci || null;
}

function parseCount(val) {
  if (val != null && typeof val === "object") {
    if (typeof val._count === "number") {
      return val._count;
    }
    if (val._count && typeof val._count === "object") {
      const nums = Object.values(val._count).filter((v) => typeof v === "number");
      if (nums.length) {
        return parseInt(nums[0], 10) || 0;
      }
    }
  }
  return parseInt(val, 10) || 0;
}

function unwrapResponseBody(body) {
  if (body == null) {
    return null;
  }
  if (Array.isArray(body)) {
    return body;
  }
  if (typeof body !== "object") {
    return body;
  }
  if (Array.isArray(body.data)) {
    return body.data;
  }
  if (Array.isArray(body.counts)) {
    return body.counts;
  }
  if (Array.isArray(body.rows)) {
    return body.rows;
  }
  if (Array.isArray(body.items)) {
    return body.items;
  }
  if (body.data && typeof body.data === "object" && !Array.isArray(body.data)) {
    return body.data;
  }
  return body;
}

/**
 * Normalizes GET /tenantUser/deleted_item_count_by_module response into
 * a map keyed by the same labels as `DELETED_ITEM_TYPES_FOR_STORE`.
 */
export function normalizeDeletedItemCountsByModule(payload) {
  const out = DELETED_ITEM_TYPES_FOR_STORE.reduce((acc, t) => ({ ...acc, [t]: 0 }), {});

  const body = unwrapResponseBody(payload);
  if (body == null) {
    return out;
  }

  if (Array.isArray(body)) {
    body.forEach((row) => {
      if (!row || typeof row !== "object") {
        return;
      }
      const mod =
        row.module ??
        row.Module ??
        row.entity ??
        row.Entity ??
        row.deleted_item_entity ??
        row.type ??
        row.Type ??
        row.name ??
        row.Name;
      const rawCount = row.count ?? row.Count ?? row.total_count ?? row.total ?? row._count;
      const canonical = resolveCanonicalModuleKey(mod);
      if (canonical) {
        out[canonical] = parseCount(rawCount);
      }
    });
    return out;
  }

  if (typeof body === "object") {
    Object.entries(body).forEach(([k, v]) => {
      if (["data", "rows", "items", "counts", "success", "message", "error"].includes(k)) {
        return;
      }
      const canonical = resolveCanonicalModuleKey(k);
      if (canonical && (typeof v === "number" || typeof v === "string" || (v && typeof v === "object"))) {
        out[canonical] = parseCount(v);
      }
    });
  }

  return out;
}
